<template>
  <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" :persistent="busy || isDirty" :value="value" content-class="acq-dialog" max-width="780"
            scrollable @click:outside="requestClose" @input="onDialogInput" @keydown.esc="requestClose">
    <v-card class="acq-dialog mdlg-scope">
      <div class="mdlg-header">
        <span class="acq-avatar acq-avatar--sm tone-info"><v-icon small style="color: inherit">{{ icon }}</v-icon></span>
        <div class="mdlg-header__text">
          <div class="acq-section-title">{{ title }}</div>
          <div class="acq-tiny text-truncate">{{ initializing ? 'Wird geladen …' : subtitle }}</div>
        </div>
        <v-btn :disabled="busy" aria-label="Schließen" icon @click="requestClose"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <v-card-text class="mdlg-body">
        <div v-if="initializing" class="d-flex flex-column py-2" style="gap: 10px">
          <v-skeleton-loader class="rounded-lg" height="32" max-width="420" type="image"/>
          <v-skeleton-loader v-for="n in 5" :key="n" class="rounded-lg" height="36" type="image"/>
        </div>
        <template v-else>
          <div v-if="noInfo && !items.length" class="soft-note">
            Bisher sind keine Angaben hinterlegt.
            <button :disabled="busy" class="add-link" type="button" @click="$emit('save', [])">
              <v-icon x-small>mdi-cancel</v-icon>Als „nicht verfügbar“ speichern
            </button>
          </div>
          <availability-week-editor v-model="items" :disabled="busy" :labels="labels"/>
          <div v-if="removable && !noInfo" class="mt-5">
            <button :disabled="busy" class="add-link remove-link" type="button" @click="confirmRemove = true">
              <v-icon x-small>mdi-delete-outline</v-icon>Alle Angaben zu Verfügbarkeiten entfernen
            </button>
          </div>
        </template>
      </v-card-text>

      <div v-if="confirmRemove" class="mdlg-footer footer--confirm">
        <span class="acq-avatar acq-avatar--sm tone-bad"><v-icon small style="color: inherit">mdi-delete-outline</v-icon></span>
        <div class="flex-grow-1" style="min-width: 0">
          <div class="footer__title">Alle Angaben entfernen?</div>
          <div class="acq-tiny">Danach gilt „keine Informationen hinterlegt“ statt „nicht verfügbar“.</div>
        </div>
        <button :disabled="removing" class="cta cta--ghost" type="button" @click="confirmRemove = false">Abbrechen</button>
        <button :disabled="removing" class="cta cta--danger" type="button" @click="$emit('remove')">
          <v-progress-circular v-if="removing" indeterminate size="14" width="2"/>
          <v-icon v-else class="cta__icon" small>mdi-delete-outline</v-icon>Entfernen
        </button>
      </div>
      <div v-else class="mdlg-footer">
        <div class="mdlg-footer__state flex-grow-1">
          <template v-if="!initializing">
            <v-icon :color="issueCount ? 'error' : undefined" small>{{ issueCount ? 'mdi-alert-circle-outline' : 'mdi-calendar-clock' }}</v-icon>
            <span :class="{ 'error--text': issueCount }" class="acq-tiny text-truncate">{{ footerText }}</span>
            <span v-if="isDirty && !issueCount" class="mdlg-dot" title="Geändert"/>
          </template>
        </div>
        <button :disabled="busy" class="cta cta--ghost" type="button" @click="requestClose">Abbrechen</button>
        <button :disabled="initializing || busy || !isDirty || issueCount > 0" class="cta cta--primary" type="button" @click="save">
          <v-progress-circular v-if="saving" indeterminate size="14" width="2"/>
          <v-icon v-else class="cta__icon" small>mdi-content-save-outline</v-icon>Speichern
        </button>
      </div>
    </v-card>
    <discard-changes-dialog v-model="showDiscard" @confirm="close"/>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import DiscardChangesDialog from '@/components/common/modern/discardChangesDialog.vue';
import AvailabilityWeekEditor from './availabilityWeekEditor.vue';
import {
  ALL_LABELS, EditableAvailability, availabilitiesKey, availabilitySummary, cloneAvailability, sortAvailabilities, validateAvailabilities,
} from './availabilityEditor.helper';

/**
 * Modern dialog shell around the availability week editor. Loading and saving stay in the entity specific wrapper:
 * emits save(items sorted), remove (= unset all, only with removable) and input(false) to close.
 */
export default Vue.extend({
  components: {AvailabilityWeekEditor, DiscardChangesDialog},
  props: {
    value: {type: Boolean, required: true},
    /** Stored availabilities; null/undefined = no information stored at all */
    availabilities: {type: Array as () => EditableAvailability[] | null, default: null},
    labels: {type: Array as () => string[], default: () => ALL_LABELS},
    title: {type: String, default: 'Verfügbarkeiten'},
    subtitle: {type: String, default: ''},
    icon: {type: String, default: 'mdi-clock-edit-outline'},
    /** Shows "Alle Angaben entfernen" (unset to null) */
    removable: {type: Boolean, default: false},
    /** Footer text when saving zero ranges */
    unavailableText: {type: String, default: 'Wird als nicht verfügbar gespeichert'},
    initializing: {type: Boolean, default: false},
    saving: {type: Boolean, default: false},
    removing: {type: Boolean, default: false},
  },
  data: () => ({
    items: [] as EditableAvailability[],
    showDiscard: false,
    confirmRemove: false,
  }),
  computed: {
    busy(): boolean {
      return this.saving || this.removing;
    },
    noInfo(): boolean {
      return !Array.isArray(this.availabilities);
    },
    isDirty(): boolean {
      return availabilitiesKey(this.items) !== availabilitiesKey(this.availabilities || []);
    },
    issueCount(): number {
      return validateAvailabilities(this.items, this.labels.length > 0).length;
    },
    footerText(): string {
      if (this.issueCount) {
        return this.issueCount === 1 ? '1 Zeitfenster ist ungültig' : `${this.issueCount} Zeitfenster sind ungültig`;
      }
      if (!this.items.length) {
        return this.noInfo ? 'Keine Angaben hinterlegt' : this.unavailableText;
      }
      return availabilitySummary(this.items);
    },
  },
  methods: {
    init() {
      this.items = (this.availabilities || []).map(cloneAvailability);
      this.confirmRemove = false;
      this.showDiscard = false;
    },
    save() {
      if (!this.isDirty || this.issueCount || this.busy) {
        return;
      }
      this.$emit('save', sortAvailabilities(this.items));
    },
    onDialogInput(open: boolean) {
      if (!open) {
        this.requestClose();
      }
    },
    requestClose() {
      if (this.busy) {
        return;
      }
      if (this.isDirty && !this.initializing) {
        this.showDiscard = true;
        return;
      }
      this.close();
    },
    close() {
      this.showDiscard = false;
      this.confirmRemove = false;
      this.$emit('input', false);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open: boolean) {
        if (open) {
          this.init();
        }
      },
    },
    availabilities(_: EditableAvailability[] | null, previous: EditableAvailability[] | null) {
      // New data from the parent (loaded / saved); keep unsaved edits of an open dialog
      if (!this.value || availabilitiesKey(this.items) === availabilitiesKey(previous || [])) {
        this.init();
      }
    },
  },
});
</script>

<style scoped>
.soft-note {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(15, 23, 42, .04);
  font-size: 13px;
}

.remove-link:hover {
  color: #C62828;
}

.footer--confirm {
  background: #FFF8F8;
}

.footer__title {
  font-size: 13px;
  font-weight: 600;
}

.theme--dark .soft-note {
  background: rgba(255, 255, 255, .06);
}

.theme--dark .footer--confirm {
  background: rgba(239, 83, 80, .08);
}
</style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/common/modern/modernDialog.css"></style>
